import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { authService, profileService } from '@/services/api';
import type { Profile } from '@/types/index';

interface AuthContextType {
  user: any | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, metadata: { username: string; display_name: string }) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  updatePassword: (password: string) => Promise<any>;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ONLINE_INTERVAL_MS = 30_000;
const ONLINE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<any | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  const startHeartbeat = () => {
    if (heartbeatTimer) return;
    void profileService.updateOnlineStatus();
    heartbeatTimer = setInterval(() => {
      void profileService.updateOnlineStatus();
    }, ONLINE_INTERVAL_MS);
  };

  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const { data } = await authService.getProfile(session.user.id);
        setProfile(data ?? null);
        startHeartbeat();
      } else {
        setProfile(null);
        stopHeartbeat();
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        authService.getProfile(session.user.id).then(({ data }) => setProfile(data ?? null));
        startHeartbeat();
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      stopHeartbeat();
    };
  }, []);

  // Pause/resume heartbeat based on tab visibility so we don't mark a user as
  // online when their tab is hidden.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startHeartbeat();
      } else {
        stopHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const signIn = (email: string, password: string) => authService.signIn(email, password);
  const signUp = (email: string, password: string, metadata: { username: string; display_name: string }) =>
    authService.signUp(email, password, metadata);
  const signOut = async () => {
    stopHeartbeat();
    await authService.signOut();
  };
  const resetPassword = (email: string) => authService.resetPassword(email);
  const updatePassword = (password: string) => authService.updatePassword(password);

  const refetch = async () => {
    if (user) {
      const { data } = await authService.getProfile(user.id);
      setProfile(data ?? null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signUp, signOut, resetPassword, updatePassword, refetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

export function isOnline(profile: Profile | null | undefined): boolean {
  if (!profile?.online_at) return false;
  return Date.now() - new Date(profile.online_at).getTime() < ONLINE_THRESHOLD_MS;
}
